import { YYYYMMDDHHmmssSSS } from "common/utils";

export function descendingComparator(a, b, orderBy, orderType, parse) {
  if (parse && parse.current) {
    const aComp = parse.current(a[orderBy]);
    const bComp = parse.current(b[orderBy]);
    if (bComp < aComp) {
      return -1;
    }
    if (bComp > aComp) {
      return 1;
    }
    return 0;
  }
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

export function getComparator(order, orderBy, orderType, parse) {
  if (orderType === "numeric" && !parse?.current) {
    return (a, b) => {
      const aVal = parseFloat(a[orderBy]);
      const bVal = parseFloat(b[orderBy]);

      const aIsNaN = isNaN(aVal);
      const bIsNaN = isNaN(bVal);

      if (aIsNaN && bIsNaN) {
        return 0;
      }
      if (aIsNaN) {
        return 1;
      }
      if (bIsNaN) {
        return -1;
      }

      let comp = 0;
      if (bVal < aVal) {
        comp = -1;
      } else if (bVal > aVal) {
        comp = 1;
      }

      return order === "desc" ? comp : -comp;
    };
  }
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy, orderType, parse)
    : (a, b) => -descendingComparator(a, b, orderBy, orderType, parse);
}

export function defaultLabelDisplayedRows({ from, to, count }) {
  return `Showing ${from} to ${to} of ${
    count !== -1 ? count : `more than ${to}`
  } entries`;
}

const NUMERIC_OPERATORS = [
  "bigger",
  "smaller",
  "biggerOrEqual",
  "smallerOrEqual",
  "equalsNr",
  "equalsNotNr",
];

function getIsNumeric(filter) {
  return NUMERIC_OPERATORS.includes(filter.operator);
}

export function getColumnFilteredData({
  data,
  columns,
  filters,
  logicalOperator = "AND",
}) {
  if (!filters || filters.length === 0) {
    return data;
  }

  const activeFilters = filters.filter(
    (f) => f.value !== "" && f.value !== undefined,
  );
  if (activeFilters.length === 0) {
    return data;
  }

  const filterFn = (item) => {
    const checkFilter = (filter) => {
      const column = columns?.find((col) => col.id === filter.column);
      const parse = column?.parseFilter || column?.parse;

      const isNumericOperator = getIsNumeric(filter);

      const rawValue = item[filter.column];
      const value = isNumericOperator
        ? parse
          ? parse(rawValue)
          : parseFloat(rawValue)
        : parse
          ? parse(rawValue)
          : rawValue;

      const queryValue = isNumericOperator
        ? parseFloat(filter.value)
        : String(filter.value).toLowerCase();

      switch (filter.operator) {
        case "containsNot":
          return !String(value ?? "")
            .toLowerCase()
            .includes(queryValue);
        case "startsWith":
          return String(value ?? "")
            .toLowerCase()
            .startsWith(queryValue);
        case "endsWith":
          return String(value ?? "")
            .toLowerCase()
            .endsWith(queryValue);
        case "equals":
          return String(value ?? "").toLowerCase() === queryValue;
        case "equalsNot":
          return String(value ?? "").toLowerCase() !== queryValue;
        case "bigger":
          return value > queryValue;
        case "smaller":
          return value < queryValue;
        case "biggerOrEqual":
          return value >= queryValue;
        case "smallerOrEqual":
          return value <= queryValue;
        case "equalsNr":
          return value === queryValue;
        case "equalsNotNr":
          return value !== queryValue;
        default: // "contains"
          return String(value ?? "")
            .toLowerCase()
            .includes(queryValue);
      }
    };

    if (logicalOperator === "OR") {
      return activeFilters.some(checkFilter);
    }
    return activeFilters.every(checkFilter);
  };

  return data.filter(filterFn);
}

export function getFilteredData({ data, searchQuery, columns, filters, logicalOperator }) {
  const columnFilteredData = getColumnFilteredData({
    data,
    columns,
    filters,
    logicalOperator,
  });
  const pattern = new RegExp(searchQuery, "i");
  const filterData = columnFilteredData.filter((item) => {
    const rowData = Object.values(item);
    return JSON.stringify(rowData).match(pattern) !== null;
  });

  return filterData;
};


export const createCVSFilename = (prefix) =>
  `${prefix}-${YYYYMMDDHHmmssSSS(new Date())}.csv`;

export const getCellClass = (cellClassName, row, column, value) => {
  let classNew;
  if (typeof cellClassName === "function") {
    classNew = cellClassName(value, row, column);
  } else if (typeof cellClassName === "string") {
    classNew = cellClassName;
  } else if (!cellClassName) {
    classNew = "";
  }
  const { onClick } = column;
  return `${classNew}${onClick ? " hasClick" : ""}`;
};


export const escapeCsvCell = (cell) => {
  const strCell = String(cell === undefined || cell === null ? "" : cell);
  if (
    strCell.includes(",") ||
    strCell.includes('"') ||
    strCell.includes("\n")
  ) {
    return `"${strCell.replace(/"/g, '""')}"`;
  }
  return strCell;
};